import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';
import { InmCarouselConfig, InmCarouselGrid } from './inm-carousel.model';
import { InmCarouselItem } from './inm-carousel-item.directive';
import { InmCarouselNext } from './inm-carousel-next.directive';
import { InmCarouselPoint } from './inm-carousel-point.directive';
import { InmCarouselPrev } from './inm-carousel-prev.directive';

const DEFAULT_CONFIG: Partial<InmCarouselConfig> = {
  grid: { xs: 1, sm: 1, md: 1, lg: 1, all: 1 },
  speed: 250,
  slide: 1,
  touch: true,
  loop: false,
  point: { visible: true },
  interval: { timing: 0 },
  variableWidth: false,
};

/** Cubic ease-in-out used for the slide animation. */
const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

@Component({
  selector: 'app-inm-carousel',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './inm-carousel.component.html',
  styleUrl: './inm-carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.can-prev]': 'canPrev()',
    '[class.can-next]': 'canNext()',
  },
})
export class InmCarousel {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly trackRef = viewChild.required<ElementRef<HTMLElement>>('track');

  readonly dataSource = input<readonly unknown[]>([]);
  readonly inputs = input<Partial<InmCarouselConfig>>({});
  readonly variableWidth = input<boolean | undefined>(undefined);

  private readonly itemTemplateRef = contentChild(InmCarouselItem, { read: TemplateRef });

  protected readonly activeIndex = signal(0);
  readonly activeIndexChange = output<number>();
  protected readonly animationState = signal(false);
  private autoplayTimer?: number;
  private readonly unsubscribe$ = new Subject<void>();

  /** Máximo índice al que se puede desplazar según cuántos items caben en pantalla. */
  private readonly maxScrollIndex = signal(0);

  protected readonly config = computed<InmCarouselConfig>(() => {
    const cfg = this.inputs() || {};
    return { ...DEFAULT_CONFIG, ...cfg } as InmCarouselConfig;
  });

  protected readonly effectiveVariableWidth = computed(() => {
    const override = this.variableWidth();
    if (override !== undefined) return override;
    return this.config().variableWidth ?? false;
  });

  protected readonly gridVars = computed(() => {
    const grid: InmCarouselGrid = this.config().grid || DEFAULT_CONFIG.grid!;
    return {
      '--grid-xs': String(grid.xs ?? grid.all ?? 1),
      '--grid-sm': String(grid.sm ?? grid.all ?? 1),
      '--grid-md': String(grid.md ?? grid.all ?? 1),
      '--grid-lg': String(grid.lg ?? grid.all ?? 1),
      '--grid-xl': String(grid.xl ?? grid.lg ?? grid.all ?? 1),
      '--grid-xxl': String(grid.xxl ?? grid.xl ?? grid.lg ?? grid.all ?? 1),
    } as Record<string, string>;
  });

  /** Hay contenido que desborda y se puede volver atrás. */
  readonly canPrev = computed(() => {
    if (this.maxScrollIndex() <= 0) return false;
    if (this.config().loop) return true;
    return this.activeIndex() > 0;
  });

  /** Hay contenido que desborda y se puede avanzar. */
  readonly canNext = computed(() => {
    if (this.maxScrollIndex() <= 0) return false;
    if (this.config().loop) return true;
    return this.activeIndex() < this.maxScrollIndex();
  });

  readonly activePoint = computed(() => {
    const slide = this.config().slide || 1;
    return Math.floor(this.activeIndex() / slide);
  });

  /** Número de posiciones de scroll reales (0..maxScrollIndex). */
  readonly pointNumbers = computed(() => {
    const count = this.dataSource().length;
    if (count === 0) return [];
    return Array.from({ length: Math.max(1, this.maxScrollIndex() + 1) }, (_, i) => i);
  });

  protected readonly itemTemplate = computed(() => this.itemTemplateRef() ?? null);

  constructor() {
    afterNextRender(() => {
      this.setupScrollListener();
      this.setupResizeObserver();
      this.measure();
      this.startAutoplay();
    });

    let lastDataKey = '';
    effect(() => {
      const data = this.dataSource();
      const hasTemplate = this.itemTemplate() !== null;
      const key = `${data.length}-${hasTemplate}-${data
        .map((item, index) => this.trackBy(index, item))
        .join('|')}`;
      if (key === lastDataKey) return;
      lastDataKey = key;

      this.activeIndex.set(0);
      this.measure();
      this.resetScroll();
      this.startAutoplay();
    });

    this.destroyRef.onDestroy(() => {
      this.stopAutoplay();
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    });
  }

  moveTo(index: number): void {
    const count = this.dataSource().length;
    if (count === 0) return;
    const target = Math.max(0, Math.min(index, this.maxScrollIndex()));
    this.activeIndex.set(target);
    this.animationState.update((state) => !state);
    this.activeIndexChange.emit(target);
    this.scrollToIndex(target, 'smooth');
    this.resetAutoplay();
  }

  next(): void {
    const count = this.dataSource().length;
    const max = this.maxScrollIndex();
    if (count === 0 || max <= 0) return;

    if (this.effectiveVariableWidth()) {
      if (this.isAtScrollEnd()) {
        if (this.config().loop) this.moveTo(0);
        return;
      }

      const target = this.getVariableWidthTarget(1);
      if (target !== this.activeIndex()) this.moveTo(target);
      return;
    }

    const slide = this.config().slide || 1;
    let target: number;

    // Preserve the last partial group before wrapping. For example, a carousel
    // showing and advancing three items with four or five entries must first
    // reach its final scroll position instead of jumping back to the start.
    if (this.config().loop && this.activeIndex() >= max) {
      target = 0;
    } else {
      target = Math.min(this.activeIndex() + slide, max);
    }

    if (target !== this.activeIndex()) this.moveTo(target);
  }

  prev(): void {
    const count = this.dataSource().length;
    const max = this.maxScrollIndex();
    if (count === 0 || max <= 0) return;

    if (this.effectiveVariableWidth()) {
      if (this.isAtScrollStart()) {
        if (this.config().loop) this.moveTo(max);
        return;
      }

      const target = this.getVariableWidthTarget(-1);
      if (target !== this.activeIndex()) this.moveTo(target);
      return;
    }

    const slide = this.config().slide || 1;
    let target: number;

    // Only wrap when navigating back from the first position. A partial last
    // group can have an index smaller than `slide`; in that case it must return
    // to the first position instead of targeting itself.
    if (this.config().loop && this.activeIndex() <= 0) {
      target = max;
    } else {
      target = Math.max(this.activeIndex() - slide, 0);
    }

    if (target !== this.activeIndex()) this.moveTo(target);
  }

  /** Gets the adjacent item from the current native scroll position. */
  private getVariableWidthTarget(direction: 1 | -1): number {
    const track = this.getTrack();
    const children = Array.from(track.children) as HTMLElement[];
    const currentScrollLeft = track.scrollLeft;
    const tolerance = 2;

    let currentIndex = 0;
    children.forEach((child, index) => {
      if (child.offsetLeft <= currentScrollLeft + tolerance) currentIndex = index;
    });

    return direction > 0
      ? Math.min(currentIndex + 1, this.maxScrollIndex())
      : Math.max(currentIndex - 1, 0);
  }

  private isAtScrollStart(): boolean {
    return this.getTrack().scrollLeft <= 1;
  }

  private isAtScrollEnd(): boolean {
    const track = this.getTrack();
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    return track.scrollLeft >= maxScrollLeft - 1;
  }

  protected trackBy(index: number, item: unknown): unknown {
    return (item as any)?.trackKey ?? (item as any)?.id ?? index;
  }

  private getTrack(): HTMLElement {
    return this.trackRef().nativeElement;
  }

  private resetScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.getTrack().scrollTo({ left: 0, behavior: 'auto' });
  }

  private measure(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const track = this.getTrack();
    const children = Array.from(track.children) as HTMLElement[];
    const count = this.dataSource().length;
    let max = 0;
    if (count && children.length) {
      if (this.effectiveVariableWidth()) {
        max = Math.max(0, count - 1);
      } else {
        const itemWidth = children[0].offsetWidth || 1;
        const visible = Math.max(1, Math.round(track.clientWidth / itemWidth));
        max = Math.max(0, count - visible);
      }
    }
    this.maxScrollIndex.set(max);
    if (this.activeIndex() > max) {
      this.activeIndex.set(max);
      this.activeIndexChange.emit(max);
      this.cdr.markForCheck();
    }
  }

  private scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const track = this.getTrack();
    const children = Array.from(track.children) as HTMLElement[];
    if (children.length === 0) return;

    const clamped = Math.max(0, Math.min(index, this.maxScrollIndex()));
    const child = children[clamped];
    if (!child) return;

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const targetLeft = Math.min(child.offsetLeft, maxScrollLeft);

    if (behavior === 'smooth') {
      this.animateScrollTo(targetLeft);
    } else {
      track.scrollTo({ left: targetLeft, behavior: 'auto' });
    }
  }

  /** Animación suave con easing propio (más controlada que el smooth nativo). */
  private animationFrameId?: number;

  private restoreScrollStyles(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const track = this.getTrack();
    track.style.scrollBehavior = '';
    track.style.scrollSnapType = '';
  }

  private animateScrollTo(targetLeft: number): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const track = this.getTrack();

    // Cancelar cualquier animación en curso (clics rápidos) y restaurar estilos.
    if (this.animationFrameId !== undefined) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
      this.restoreScrollStyles();
    }

    const startLeft = track.scrollLeft;
    const distance = targetLeft - startLeft;
    if (Math.abs(distance) < 1) return;

    const duration = this.config().speed ?? 250;
    const startTime = performance.now();
    // Desactivar temporalmente smooth nativo y scroll-snap para que la tween no pelee
    // con el snap del track (causa "avanza y se regresa").
    track.style.scrollBehavior = 'auto';
    track.style.scrollSnapType = 'none';

    const step = (now: number) => {
      const progress = Math.min(1, (now - startTime) / duration);
      track.scrollLeft = startLeft + distance * easeInOutCubic(progress);
      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(step);
      } else {
        this.animationFrameId = undefined;
        this.restoreScrollStyles();
        // Re-afirmar la posición final tras restaurar el snap (evita que el navegador
        // re-snappee a un punto equivocado y "reinicie" el carousel).
        track.scrollTo({ left: targetLeft, behavior: 'auto' });
      }
    };

    this.animationFrameId = requestAnimationFrame(step);
  }

  private setupScrollListener(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const track = this.getTrack();

    fromEvent(track, 'scroll')
      .pipe(debounceTime(80), takeUntil(this.unsubscribe$))
      .subscribe(() => this.updateActiveIndexFromScroll());
  }

  private updateActiveIndexFromScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const track = this.getTrack();
    const children = Array.from(track.children) as HTMLElement[];
    if (children.length === 0) return;

    const scrollLeft = track.scrollLeft;
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    let index = 0;

    if (this.effectiveVariableWidth()) {
      // With variable-width items, use the first visible item as the active one.
      // Using the viewport center makes the index jump over several chips when
      // multiple items are visible and prevents navigating back reliably at the end.
      const tolerance = 1;
      for (let i = 0; i < children.length; i++) {
        const left = children[i].offsetLeft;
        if (left > scrollLeft + tolerance) break;
        index = i;
      }
    } else {
      const itemWidth = children[0].offsetWidth || 1;
      index = Math.round(scrollLeft / itemWidth);
    }

    // Si se llegó al fondo del track, asegurar el último índice (evita el estado "fantasma").
    if (scrollLeft >= maxScrollLeft - 1) {
      index = this.maxScrollIndex();
    }

    index = Math.max(0, Math.min(index, this.maxScrollIndex()));

    if (index !== this.activeIndex()) {
      this.activeIndex.set(index);
      this.activeIndexChange.emit(index);
      this.cdr.markForCheck();
    }
  }

  private setupResizeObserver(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (typeof ResizeObserver === 'undefined') return;
    const track = this.getTrack();
    const observer = new ResizeObserver(() => {
      this.measure();
      this.cdr.markForCheck();
    });
    observer.observe(track);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  private startAutoplay(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.stopAutoplay();

    const cfg = this.config();
    if (!cfg.loop) return;

    const timing = cfg.interval?.timing ?? 0;
    if (!timing || timing < 1000) return;

    const count = this.dataSource().length;
    if (count <= 1) return;

    this.autoplayTimer = window.setInterval(() => this.next(), timing);
  }

  private stopAutoplay(): void {
    if (this.autoplayTimer !== undefined) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = undefined;
    }
  }

  private resetAutoplay(): void {
    this.stopAutoplay();
    this.startAutoplay();
  }
}
